import React, { useEffect, useState } from "react";
import { useMsal } from '@azure/msal-react';
import { createStatisticsService } from "../../utils/feedback/statistics.service";
import { UsersIcon, NewUsersIcon, TotalQueriesIcon } from "./StatisticsIcons";
import HorizontalBarChart from "./HorizontalBarChart";

const PersonIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

const GlobeIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10A15.3 15.3 0 0 1 8 12a15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const THREE_HOURS_MS = 3 * 60 * 60 * 1000;

/**
 * StatisticsPanel
 * Fetches and displays statistics data. Shows a spinner while loading.
 */
const StatisticsPanel: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [scope, setScope] = useState<'global' | 'personal'>('global');

  const { instance: msalInstance } = useMsal();

  const fetchData = React.useCallback((showSpinner: boolean, currentScope: 'global' | 'personal') => {
    let mounted = true;
    const statisticsService = createStatisticsService(msalInstance);
    statisticsService.getStatistics(currentScope).then(
      (result) => {
        if (!mounted) return;
        if (result?.horizontalBars?.labels && result?.horizontalBars?.values) {
          const paired = (result.horizontalBars.labels as string[]).map((label: string, i: number) => ({
            label,
            value: result.horizontalBars.values[i] as number,
          }));
          paired.sort((a, b) => b.value - a.value);
          result.horizontalBars.labels = paired.map(p => p.label);
          result.horizontalBars.values = paired.map(p => p.value);
        }
        setData(result);
        setLastUpdated(new Date());
        setError(null);
        if (showSpinner) setLoading(false);
      },
      (err) => {
        if (!mounted) return;
        setError(err?.message || "Failed to load statistics");
        if (showSpinner) setLoading(false);
      }
    );
    return () => { mounted = false; };
  }, [msalInstance]);

  useEffect(() => {
    // Only the initial fetch and the latest interval tick can still be in-flight,
    // so track just those two cleanups instead of accumulating one per tick.
    let latestTickCleanup = fetchData(true, scope);
    const interval = setInterval(() => {
      latestTickCleanup = fetchData(false, scope);
    }, THREE_HOURS_MS);
    return () => {
      clearInterval(interval);
      latestTickCleanup();
    };
  }, [fetchData, scope]);

  const handleScopeChange = (newScope: 'global' | 'personal') => {
    if (newScope === scope) return;
    // Prime the loading/error state here (in the event handler) so the useEffect
    // triggered by the scope change doesn't need to call setState synchronously
    // in its body. `loading` is already `true` on initial mount via useState(true).
    setLoading(true);
    setError(null);
    // Clear the previous scope's data. If the new fetch fails, `data` stays null
    // so the chart/KPIs don't render — otherwise the (stale) chart would render
    // behind the error overlay and its z-indexed bars would paint on top of it.
    // During the successful path, `contentFadeStyle` fades the content to
    // opacity 0 while `loading` is true anyway, so clearing here isn't visible.
    setData(null);
    setScope(newScope);
  };

  // Accepts a string in UTC (e.g., 2026-05-13T09:52:53.490000) or a Date object
  const formatLastUpdated = (input: string | Date) => {
    let date: Date;
    if (typeof input === 'string') {
      // Remove extra microseconds if present (e.g., .490000 → .490)
      const cleaned = input.replace(/\.(\d{3})\d+$/, '.$1');
      // Parse as UTC
      date = new Date(cleaned + 'Z');
    } else {
      date = input;
    }
    // Now is local time
    const now = new Date();
    const isToday = date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();
    if (isToday) {
      return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    }
    return date.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  };

  // Derive KPI cards based on scope
  const globalCards = [
    { label: 'Total Users', icon: <UsersIcon size={42} />, value: data?.cards?.find((c: any) => c.title === 'Unique Users')?.value },
    { label: 'New Users', icon: <NewUsersIcon size={35} />, value: data?.cards?.find((c: any) => c.title === 'New Users')?.value },
    { label: 'Total Queries', icon: <TotalQueriesIcon size={32} />, value: data?.cards?.find((c: any) => c.title === 'Total Queries')?.value },
  ];

  const personalCards = [
    { label: 'Total Queries', icon: <TotalQueriesIcon size={32} />, value: data?.cards?.find((c: any) => c.title === 'Total Queries')?.value },
  ];

  const kpiCards = scope === 'global' ? globalCards : personalCards;

  const refreshedOn = data?.cards?.find((c: any) => c.title === 'Refreshed On')?.value;

  const FADE_MS = 250;
  const contentFadeStyle: React.CSSProperties = {
    opacity: loading ? 0 : 1,
    transition: `opacity ${FADE_MS}ms ease`,
  };

  return (
    <div className="flex flex-col h-full w-full bg-transparent border-t" style={{ borderColor: 'var(--dark-color-border-color)', minHeight: 320 }}>
      <div className="flex flex-1 min-h-0 relative">
        {/* Loading spinner — fades in/out over the panels while their content fades out/in */}
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center pointer-events-none"
          style={{
            opacity: loading ? 1 : 0,
            transition: `opacity ${FADE_MS}ms ease`,
          }}
        >
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500 mb-4" />
          <div className="text-base text-center" style={{ color: 'var(--dark-color-text)' }}>Loading metrics…</div>
        </div>
        {/* Error overlay — z-30 so it sits above the chart's internal stacking
             (HorizontalBarChart uses z-10 on the bars container and z-20 on the
             value labels). Row 1 doesn't create an isolating stacking context,
             so a z-10 here would tie with the bars container's z-10 and lose
             the DOM-order tiebreak. */}
        {!loading && error && (
          <div
            className="absolute inset-0 z-30 flex items-center justify-center"
            style={{ background: 'var(--dark-main-background)' }}
          >
            <div className="text-xl font-semibold text-center px-8" style={{ color: 'var(--dark-color-text)' }}>
              Cannot fetch Navigator metrics. Please try again later.
            </div>
          </div>
        )}
      {/* Left: KPIs */}
      <div
        className="w-1/4 shrink-0 flex flex-col items-start justify-start pt-8 pr-6 pb-8 pl-8 border-r"
        style={{ background: 'var(--dark-main-background)', borderColor: 'var(--dark-color-border-color)' }}
      >
        <div className="w-full flex-1 flex flex-col" style={contentFadeStyle}>
          <div
            className="text-sm mb-3 text-left w-full"
            style={{ color: 'var(--dark-color-text)' }}
          >
            Total usage for 2026
          </div>
          {/* KPI cards container — centered in remaining space */}
          <div className="flex-1 flex flex-col justify-between w-full py-2">
            {kpiCards.map((card, idx) => (
              <React.Fragment key={card.label}>
                {idx > 0 && <div className="w-full h-px" style={{ background: 'var(--dark-color-border-color)' }} />}
                <div className="flex items-center w-full px-6 py-6">
                  {card.icon}
                  <div className="ml-4">
                    <div className="text-xs font-medium" style={{ color: 'var(--dark-color-text)' }}>{card.label}</div>
                    <div className="text-lg font-bold" style={{ color: 'var(--dark-color-text)' }}>{typeof card.value === 'number' ? card.value.toLocaleString() : '-'}</div>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      {/* Right: Bar Chart */}
      <div
        className="flex-1 flex flex-col items-center justify-center pl-14 pr-12 py-8 relative"
        style={{ background: 'var(--dark-color-accent)' }}
      >
        <div className="w-full h-full flex flex-col items-center justify-center" style={contentFadeStyle}>
          {/* Last updated timestamp — top-right corner, vertically centered with the X button */}
          {lastUpdated && (
            <div
              className="absolute top-4 right-12 text-sm opacity-60 flex items-center"
              style={{ color: 'var(--dark-color-text)', height: '1.75rem' }}
            >
              Last Updated: {formatLastUpdated(refreshedOn ?? lastUpdated)}
            </div>
          )}
          {data?.horizontalBars ? (
            <HorizontalBarChart
              labels={data.horizontalBars.labels}
              values={data.horizontalBars.values}
              title={data.horizontalBars.title || "Top Capabilities"}
            />
          ) : null}
        </div>
      </div>
      </div>
      {/* Bottom toggle: Personal / Global */}
      <div
        className="flex items-center justify-center py-3"
        style={{ background: 'var(--dark-main-background)' }}
      >
        <div
          className="relative flex items-center rounded-md overflow-hidden border"
          style={{
            background: 'var(--color-neutral-gray)',
            borderColor: 'var(--dark-color-border-color)',
          }}
        >
          {/* Sliding tab indicator — flush with the container edges */}
          <span
            aria-hidden="true"
            className="absolute top-0 bottom-0"
            style={{
              background: 'var(--dark-color-accent-blue)',
              width: '50%',
              left: scope === 'personal' ? '0' : '50%',
              transition: 'left 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />
          <button
            type="button"
            onClick={() => handleScopeChange('personal')}
            aria-pressed={scope === 'personal'}
            className="relative flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium"
            style={{
              color: 'var(--dark-color-text)',
              opacity: scope === 'personal' ? 1 : 0.55,
              transition: 'opacity 0.25s ease',
              zIndex: 1,
            }}
          >
            <PersonIcon size={16} />
            Personal
          </button>
          <button
            type="button"
            onClick={() => handleScopeChange('global')}
            aria-pressed={scope === 'global'}
            className="relative flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium"
            style={{
              color: 'var(--dark-color-text)',
              opacity: scope === 'global' ? 1 : 0.55,
              transition: 'opacity 0.25s ease',
              zIndex: 1,
            }}
          >
            <GlobeIcon size={16} />
            Global
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatisticsPanel;